import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { matchPath } from 'react-router-dom';

import {
  registrationNotCompletedSelector,
  userMissingDetailsSelector,
  userNeedsEmailVerificationSelector,
  userNeedsFirstTermsConsentSelector,
} from '@lootboy/lib/selectors';

import { useTimeout } from '~/hooks/useTimeout';
import { clearRouteAfterLogin } from '~/redux/slices/location';
import { useRouterPaths } from '~/redux/slices/location/hooks';
import { routeAfterLoginSelector } from '~/redux/slices/location/selectors';
import { TAppStore, useAppDispatch } from '~/redux/store/useAppDispatch';
import { loginFlowPaths } from '~/routes/MenuRouter/menuRoutes';
import {
  EMAIL_VERIFICATION_PATH,
  HOME_PATH,
  PASSWORD_RESET_PATH,
  REGISTER_TERMS_PATH,
  REQUEST_EMAIL_VERIFICATION_PATH,
  USER_DETAILS_PATH,
} from '~/routes/paths';
import { useAppHistory } from '~/utilities/routing';

enum EWelcomeMessage {
  EXISTING_USER = 'welcome_existing_user',
  NEW_PASSWORD = 'ResetPasswordView_reset_succesful',
  NEW_USER = 'welcome_new_user',
}

export default function useLoginFlow(
  close: () => void,
  previousIsLoggedIn: boolean,
): string {
  const history = useAppHistory();
  const dispatch = useAppDispatch();
  const routeAfterLogin = useSelector(routeAfterLoginSelector);
  const { main } = useRouterPaths();
  const { timeout } = useTimeout();
  const registrationNotCompleted = useSelector<TAppStore, boolean>(
    registrationNotCompletedSelector,
  );
  const initialRegistrationNotCompleted = useRef(registrationNotCompleted);
  const [message, setMessage] = useState('');

  const consentNeeded = useSelector(userNeedsFirstTermsConsentSelector);
  const emailVerificationNeeded = useSelector(
    userNeedsEmailVerificationSelector,
  );
  const missingDetails = useSelector(userMissingDetailsSelector);
  const PROFILE_SETUP_ROUTES: Record<string, boolean> = useMemo(
    () => ({
      [REGISTER_TERMS_PATH]: consentNeeded,
      [REQUEST_EMAIL_VERIFICATION_PATH]: emailVerificationNeeded,
      [USER_DETAILS_PATH]: missingDetails,
    }),
    [consentNeeded, emailVerificationNeeded, missingDetails],
  );

  const { pathname } = history.location;

  useEffect(() => {
    if (previousIsLoggedIn || message) {
      if (matchPath(pathname, { path: loginFlowPaths })) {
        history.replace(HOME_PATH);
      }
    } else if (!registrationNotCompleted) {
      if (
        initialRegistrationNotCompleted.current !== registrationNotCompleted
      ) {
        setMessage(EWelcomeMessage.NEW_USER);
      } else if (pathname === PASSWORD_RESET_PATH) {
        setMessage(EWelcomeMessage.NEW_PASSWORD);
      } else {
        setMessage(EWelcomeMessage.EXISTING_USER);
      }

      history.replace(routeAfterLogin || main);
      dispatch(clearRouteAfterLogin());
      timeout(2000, close);
    } else {
      const setupPaths = Object.keys(PROFILE_SETUP_ROUTES);
      if (
        !matchPath(pathname, {
          path: [...setupPaths, EMAIL_VERIFICATION_PATH],
        })
      ) {
        const profileSetupPath = setupPaths.find(
          (path) => PROFILE_SETUP_ROUTES[path],
        );
        if (profileSetupPath) {
          history.replace(profileSetupPath);
        }
      }
    }
  }, [
    PROFILE_SETUP_ROUTES,
    close,
    dispatch,
    history,
    main,
    message,
    pathname,
    previousIsLoggedIn,
    registrationNotCompleted,
    routeAfterLogin,
    timeout,
  ]);

  return message;
}
